import React from "react";
import type { ApolloClient } from "@apollo/client";
import { toast } from "react-toastify";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import { isEmpty, get } from "lodash";
import { Row, Col, Card } from "react-bootstrap";
import { FiAlertTriangle } from "react-icons/fi";
import { bankSelector, isBankVerifiedSelector } from "store/selectors/bank";
import { payrollPeriodsToEnglishMapping } from "utils/timeHelper";
import { getTransactions } from "actions/transactionActions";
import { transactionConstants, bankConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getIraAccountIdSelector } from "store/selectors/user";
import { deleteBank } from "actions/bankActions";
import type { RootState } from "types/state";
import type { UserBank } from "types/models";

import IconCircle from "components/IconCircle";
import IconHeader from "components/IconHeader";
import Alert from "components/Alert";
import PlaidLinkUpdateCard from "../../../../components/PlaidLinkUpdateCard";
import ReAuthPlaidLink from "components/ReAuthPlaidLink";
import ContributionSource from "./ContributionSource";
import AddBankCard from "pages/dashboards/AddBankCard";
import ContributionModal from "pages/dashboards/individualDashboard/dashboard/ContributionModal";
import OneTimeContributionCard from "./OneTimeContributionCard";
import Button from "components/Button";
import { recurringContributionSelector } from "store/selectors/contribution";
import { activeInvestmentAccountsSelector } from "store/selectors/accounts";

const actions = [
  transactionConstants.GET_TRANSACTIONS,
  bankConstants.OAUTH_PLAID_LINK_TOKEN,
  bankConstants.DELETE_BANK,
];

// PRESERVED BUG (conversion-only): `actions` is already an array, so `[actions]`
// double-wraps it into string[][]. The selector stringifies each inner array as
// a key (`loading.A,B,C`), which never matches, so these selectors are inert.
// Kept verbatim to avoid behavior change.
const loadingSelector = createLoadingSelector(
  // @ts-expect-error -- preserved double-wrapped array (string[][]); see note above
  [actions]
);
const errorSelector = createErrorSelector(
  // @ts-expect-error -- preserved double-wrapped array (string[][]); see note above
  [actions]
);

const mapStateToProps = (state: RootState) => {
  const bank = bankSelector(state);
  const individualReAuthRequired = !!get(bank, "loginRequired");

  return {
    bank,
    error: errorSelector(state),
    bankIsVerified: isBankVerifiedSelector(state),
    isFetching: loadingSelector(state),
    iraAccountId: getIraAccountIdSelector(state),
    transactions: state.transactions.transactions,
    individualReAuthRequired,
    recurringContribution: recurringContributionSelector(state),
    accounts: activeInvestmentAccountsSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getTransactions,
  deleteBank,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // `isLoading` is read in render but never supplied by the connector
  // (mapStateToProps produces `isFetching`); preserved as a parent-passed prop.
  isLoading?: boolean;
  onSuccess?: (
    token: unknown,
    metadata: unknown,
    userLegalName: unknown
  ) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  showOneTimeContributionModal: boolean;
  initialFetching: boolean;
  isDeleting: boolean;
}

class Banks extends React.PureComponent<Props, State> {
  _removeInFlight = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      showOneTimeContributionModal: false,
      initialFetching: true,
      isDeleting: false,
    };
  }

  componentDidMount() {
    void Promise.all([
      this.props.getTransactions(
        this.props.client as ApolloClient<object>,
        this.props.iraAccountId as string
      ),
    ]).then(() => this.setState({ initialFetching: false }));
  }

  _removeBankLink = () => {
    // PRESERVED BUG (conversion-only): original read `this.props.bank.id`
    // directly; `bank` may be undefined, so keep the throw-on-undefined access.
    const bankId = this.props.bank!.id;
    if (this._removeInFlight) {
      return;
    }
    this._removeInFlight = true;
    void this.props
      .deleteBank(this.props.client as ApolloClient<object>, bankId)
      .then(() => {
        if (!this.props.error) {
          toast.success("Successfully removed your bank account.");
        }
      })
      .finally(() => {
        this._removeInFlight = false;
      });
  };

  _getBankRow = () => {
    const { bank } = this.props;
    if (isEmpty(bank)) return null;

    // isEmpty does not narrow `UserBank | undefined`; the guard above already
    // returns for an empty bank, so assert non-null to keep direct access.
    const contributionSource = {
      bankName: bank!.bankName,
      accountSubtype: bank!.accountSubtype,
      accountId: bank!.accountId,
      id: bank!.id,
    };
    const recurringContribution = this.props.recurringContribution;
    let contributionInfo;

    if (!recurringContribution) {
      contributionInfo = {};
    } else {
      const targetAccount = this.props.accounts.find(
        (account) => account.id === recurringContribution.targetIraAccountId
      );
      contributionInfo = {
        ...recurringContribution,
        schedule:
          payrollPeriodsToEnglishMapping[
            recurringContribution.frequency as keyof typeof payrollPeriodsToEnglishMapping
          ],
        end: recurringContribution.nextScheduledDate,
        anchorPayDate: recurringContribution.nextScheduledDate,
        accountId: recurringContribution.targetIraAccountId,
        accountType: get(targetAccount, "accountType"),
      };
    }

    const bankIsPendingFinalVerification =
      bank!.state === "BankAccountVerified";

    if (!this.props.bankIsVerified) return null;

    return (
      <Col md={12}>
        <ContributionSource
          isEmployer={false}
          contributionSource={contributionSource}
          contributionInfo={contributionInfo}
          isPendingVerification={bankIsPendingFinalVerification}
        />

        <OneTimeContributionCard />
      </Col>
    );
  };

  _handleOnSuccess = (
    token: unknown,
    metadata: unknown,
    userLegalName: unknown
  ) => {
    if (this.props.onSuccess) {
      this.props.onSuccess(token, metadata, userLegalName);
      toast.success("Bank account successfully re-authenticated.");
    }
  };

  _getPlaidLinkUpdateCard = (bank: UserBank | undefined) => {
    return (
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div style={{ marginRight: "10px" }}>
          <Button
            size="sm"
            color="red"
            name="action"
            btnLabel="Remove Bank"
            onClick={() => this.setState({ isDeleting: true })}
          />
        </div>
        <div>
          <ReAuthPlaidLink bank={bank} onSuccess={this._handleOnSuccess} />
        </div>
      </div>
    );
  };

  _handleRemoveBank = () => {
    const header = "Are you sure you want to remove your bank account?";
    return (
      <>
        <Card>
          <Card.Body>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <IconCircle
                type="error"
                icon={
                  <FiAlertTriangle color="white" stroke="#B12121" size={16} />
                }
              />
            </div>
            <IconHeader variant="centeredText" headerText={header} />

            {!isEmpty(this.props.error) && (
              <Alert type="error" msg={this.props.error} />
            )}
            <div className="remove-bank-action">
              <div className="bank-cancel">
                <Button
                  size="sm"
                  color="cancel"
                  name="cancel"
                  btnLabel="Cancel"
                  onClick={() => this.setState({ isDeleting: false })}
                />
              </div>
              <Button
                size="sm"
                color="red"
                name="submit"
                btnLabel="Remove"
                onClick={this._removeBankLink}
                withArrow={true}
                loading={this.props.isLoading}
              />
            </div>
          </Card.Body>
        </Card>
      </>
    );
  };

  render() {
    const bankRow = this._getBankRow();
    const showAddBankCard = isEmpty(bankRow) || !this.props.bankIsVerified;

    const { bank } = this.props;

    const bankAccountName = get(bank, "bankName");
    const lastFourDigits = get(bank, "accountId");
    const description = `Re-authenticate your ${bankAccountName} bank account ending in ${lastFourDigits} to resume payroll deductions.`;

    return (
      <div className="banks-container">
        {this.state.showOneTimeContributionModal && (
          <ContributionModal
            show={this.state.showOneTimeContributionModal}
            onSuccess={() => {
              this.setState({ showOneTimeContributionModal: false });
            }}
            onClose={() => {
              this.setState({ showOneTimeContributionModal: false });
            }}
            depositOnly={true}
          />
        )}
        <div className="bank-row-header">
          <h1 className="section-title">Bank Account</h1>
        </div>

        {showAddBankCard && (
          <Row>
            <Col md={6}>
              <AddBankCard />
            </Col>
          </Row>
        )}

        {this.props.individualReAuthRequired ? (
          <Row>
            <Col md={6}>
              {this.state.isDeleting ? (
                this._handleRemoveBank()
              ) : (
                <PlaidLinkUpdateCard
                  icon={
                    <FiAlertTriangle color="white" stroke="#B12121" size={16} />
                  }
                  title="Bank Re-Authentication Required!"
                  description={description}
                  color="red"
                  // @ts-expect-error -- inert prop: PlaidLinkUpdateCard has no `withArrow`; preserved from original JS
                  withArrow={true}
                  onClick={this._getPlaidLinkUpdateCard(bank)}
                />
              )}
            </Col>
          </Row>
        ) : (
          <> {bankRow}</>
        )}
      </div>
    );
  }
}

export default connector(withApollo<OwnProps & PropsFromRedux>(Banks));
